import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { const c = localStorage.getItem("stancor_me"); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [permissions, setPermissions] = useState(() => {
    try { const c = localStorage.getItem("stancor_me"); return c ? (JSON.parse(c).permissions || {}) : {}; } catch { return {}; }
  });
  const [loading, setLoading] = useState(() => !(localStorage.getItem("stancor_token") && localStorage.getItem("stancor_me")));

  const fetchMe = useCallback(async () => {
    try {
      const token = localStorage.getItem("stancor_token");
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await api.get("/auth/me");
      setUser(res.data);
      setPermissions(res.data.permissions || {});
      localStorage.setItem("stancor_me", JSON.stringify(res.data));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("stancor_token");
        localStorage.removeItem("stancor_user");
        localStorage.removeItem("stancor_me");
        setUser(null);
        setPermissions({});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.requires_2fa) {
      return res.data; // Return so Login page can handle 2FA step
    }
    localStorage.setItem("stancor_token", res.data.token);
    localStorage.setItem("stancor_user", JSON.stringify(res.data.user));
    if (res.data.db) localStorage.setItem("stancor_db", res.data.db);
    if (res.data.db_display_name) localStorage.setItem("stancor_db_label", res.data.db_display_name);
    setUser(res.data.user);
    await fetchMe();
    window.dispatchEvent(new Event("stancor-auth-change"));
    return res.data;
  };

  const complete2FALogin = async (tempToken, code) => {
    const res = await api.post("/security-intel/2fa/verify-login", { temp_token: tempToken, code });
    localStorage.setItem("stancor_token", res.data.token);
    localStorage.setItem("stancor_user", JSON.stringify(res.data.user));
    if (res.data.db) localStorage.setItem("stancor_db", res.data.db);
    if (res.data.db_display_name) localStorage.setItem("stancor_db_label", res.data.db_display_name);
    setUser(res.data.user);
    await fetchMe();
    window.dispatchEvent(new Event("stancor-auth-change"));
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("stancor_token");
    localStorage.removeItem("stancor_user");
    localStorage.removeItem("stancor_me");
    setUser(null);
    setPermissions({});
    window.dispatchEvent(new Event("stancor-auth-change"));
  };

  const isSuperAdmin = user?.is_superadmin || user?.role_name === "Admin";

  const hasPermission = (module, action) => {
    // Superadmin bypass
    if (isSuperAdmin) return true;
    // Direct match
    if (permissions[module]?.includes(action)) return true;
    // Parent inheritance: if checking "sales.pricelists", also check "sales"
    if (module.includes(".")) {
      const parent = module.split(".")[0];
      if (permissions[parent]?.includes(action)) return true;
    }
    return false;
  };

  const isAdmin = isSuperAdmin;

  return (
    <AuthContext.Provider value={{ user, permissions, loading, login, complete2FALogin, logout, hasPermission, isAdmin, isSuperAdmin, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
